// Global Instructions Rule Applied!
//
// The company's highlight atoms across periods, on the company Agent tab
// (docs/AGENT_PROGRAMME_LP_HIGHLIGHTS_DESIGN.md Phase 5 "Company Agent tab
// HighlightsSection"). Chrome: the LpBriefCard skeleton (FormSection,
// hideWhenEmpty until the agent has written something) around a read-only
// antd Table; a row opens the set as a form tab like the Period Highlights
// list does.
import React, { useCallback, useEffect, useState } from 'react'
import { Button, Table, Tag } from 'antd'
import dayjs from 'dayjs'
import { FormSection } from '../../../core/components/FormSection.tsx'
import { useTabManagerContext } from '../../../core/app-layout/lib/TabManagerContext.tsx'
import { store } from '../../../state_manager/store.ts'
import { getAccessToken } from '../../../core/auth/authService'
import type { HighlightSetRecord } from '../../../state_manager/data_models/period_highlights_model.ts'
import { fetchCompanyHighlightSets } from '../services/periodHighlightsService.ts'
import { STATUS_TAG, versionTag } from './PeriodHighlightsPage.tsx'

const token = () => getAccessToken(store.getState().auth)

interface CompanyHighlightsSectionProps {
  accountId: string
  onRefreshReady?: ((refreshFn: () => Promise<void>) => void) | null
  hideWhenEmpty?: boolean
}

export const CompanyHighlightsSection: React.FC<CompanyHighlightsSectionProps> = ({ accountId, onRefreshReady, hideWhenEmpty = false }) => {
  const { openFormInTab } = useTabManagerContext()
  const [rows, setRows] = useState<HighlightSetRecord[]>([])
  const [loading, setLoading] = useState(false)
  const [loaded, setLoaded] = useState(false)

  const load = useCallback(async () => {
    if (!accountId) return
    setLoading(true)
    try {
      setRows(await fetchCompanyHighlightSets(accountId, token()))
    } catch (error) {
      console.error('[period_highlights] company atoms load failed:', error)
    } finally {
      setLoading(false)
      setLoaded(true)
    }
  }, [accountId])

  useEffect(() => { load() }, [load])
  useEffect(() => { onRefreshReady?.(load) }, [onRefreshReady, load])

  if (hideWhenEmpty && loaded && rows.length === 0) return null

  const openSet = (r: HighlightSetRecord) =>
    openFormInTab('period-highlights', r.highlight_sets__id, `${r.prog__scope_name ?? 'Highlights'} · ${r.prog__period_label}`)

  return (
    <FormSection
      data-component='CompanyHighlightsSection'
      title='Period highlights'
      titleColor='blue'
      loading={loading}
      className='shadow-sm border border-gray-200'
      headerActions={<span className='text-gray-400 text-[11.5px]'>{rows.length} period{rows.length === 1 ? '' : 's'} · the live set grows as the follower reads; finalise freezes it into the atom the fund and LP sets use</span>}
    >
      <Table<HighlightSetRecord>
        size='small'
        rowKey='highlight_sets__id'
        dataSource={rows}
        pagination={rows.length > 8 ? { pageSize: 8, size: 'small' } : false}
        locale={{ emptyText: 'Nothing flagged yet — the follower writes a live set for the open period as it reads; a fund or LP Generate finalises it into an atom.' }}
        columns={[
          { title: 'Period', dataIndex: 'prog__period_label', width: 130 },
          {
            title: 'Version', key: 'version', width: 120,
            render: (_: unknown, r) => { const t = versionTag(r); return <Tag color={t.color}>{t.label}</Tag> }
          },
          {
            title: 'Status', dataIndex: 'highlight_sets__status', width: 110,
            render: (v: string) => { const t = STATUS_TAG[v] || { color: 'default', label: v }; return <Tag color={t.color}>{t.label}</Tag> }
          },
          { title: 'Items', dataIndex: 'prog__included_count', width: 70, align: 'right' as const },
          { title: 'Summary', dataIndex: 'highlight_sets__summary', ellipsis: true, render: (v: string | null) => v || <span className='text-gray-400'>—</span> },
          { title: 'Generated', dataIndex: 'highlight_sets__generated_at', width: 110, render: (v: string | null) => v ? dayjs(v).format('MM/DD/YYYY') : '—' },
          { title: '', key: 'open', width: 80, render: (_: unknown, r) => <Button size='small' onClick={() => openSet(r)}>Open</Button> }
        ]}
      />
    </FormSection>
  )
}

export default CompanyHighlightsSection
